import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, CheckCircle2, CircleAlert, MailCheck, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';

import { auth } from '../lib/api';
import { BrandLogoWithWordmark } from '../components/BrandLogo';
import { Button, Field, Input } from '../components/ui-kit';

const PENDING_EMAIL_STORAGE_KEY = 'td:pending-email-verification';

const readPendingVerificationEmail = () => {
  if (typeof window === 'undefined') return '';
  try {
    return (
      window.sessionStorage.getItem(PENDING_EMAIL_STORAGE_KEY) ||
      window.localStorage.getItem(PENDING_EMAIL_STORAGE_KEY) ||
      ''
    );
  } catch (error) {
    return '';
  }
};

const clearPendingVerificationEmail = () => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(PENDING_EMAIL_STORAGE_KEY);
    window.localStorage.removeItem(PENDING_EMAIL_STORAGE_KEY);
  } catch (error) {
    // Storage can be unavailable in private or embedded contexts.
  }
};

const VerificationShell = ({ children }) => (
  <main className="min-h-screen bg-[#090d16] text-zinc-50 px-4 py-8 flex items-center justify-center">
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      <div className="absolute -top-28 -left-24 h-80 w-80 rounded-full bg-lime-300/10 blur-3xl" />
      <div className="absolute top-16 right-[-8rem] h-96 w-96 rounded-full bg-cyan-300/10 blur-3xl" />
      <div className="absolute inset-x-0 top-1/2 h-px bg-lime-300/20" />
      <div className="absolute left-1/2 top-0 h-full w-px bg-white/10" />
    </div>
    <section className="relative w-full max-w-lg rounded-2xl border border-white/10 bg-zinc-950/78 p-6 shadow-2xl shadow-black/30 backdrop-blur md:p-8">
      <div className="mb-8 flex justify-center">
        <BrandLogoWithWordmark size={44} />
      </div>
      {children}
    </section>
  </main>
);

const getStatusView = (status, t) => {
  if (status === 'success' || status === 'already_verified') {
    return {
      icon: <CheckCircle2 size={34} strokeWidth={1.7} />,
      tone: 'text-lime-300 bg-lime-300/10 border-lime-300/25',
      title: t('auth.emailVerifiedTitle', 'Email verified'),
      body: t('auth.emailVerifiedBody', 'Your email is confirmed. You can continue using Tennis Diary.'),
    };
  }
  if (status === 'expired') {
    return {
      icon: <CircleAlert size={34} strokeWidth={1.7} />,
      tone: 'text-amber-200 bg-amber-200/10 border-amber-200/25',
      title: t('auth.emailExpiredTitle', 'Verification link expired'),
      body: t('auth.emailExpiredBody', 'Request a fresh confirmation link and try again.'),
    };
  }
  if (status === 'loading') {
    return {
      icon: <RefreshCcw size={34} strokeWidth={1.7} className="animate-spin" />,
      tone: 'text-cyan-200 bg-cyan-200/10 border-cyan-200/25',
      title: t('auth.emailVerifyingTitle', 'Verifying your email'),
      body: t('auth.emailVerifyingBody', 'This should only take a moment.'),
    };
  }
  return {
    icon: <CircleAlert size={34} strokeWidth={1.7} />,
    tone: 'text-red-200 bg-red-200/10 border-red-200/25',
    title: t('auth.emailInvalidTitle', 'Verification link is invalid'),
    body: t('auth.emailInvalidBody', 'The link may have already been used or copied incorrectly.'),
  };
};

export const CheckEmailPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const initialEmail = location.state?.email || readPendingVerificationEmail();
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [status, setStatus] = useState('idle');
  const [verifying, setVerifying] = useState(false);
  const wasSent = location.state?.verificationEmailSent !== false;
  const [resending, setResending] = useState(false);

  const normalizedEmail = email.trim();
  const normalizedCode = code.trim();
  const canSubmit = normalizedEmail && normalizedCode.length === 6 && !verifying;
  const isVerified = status === 'success' || status === 'already_verified';

  const handleCodeChange = (event) => {
    const nextCode = event.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(nextCode);
    if (status !== 'idle') {
      setStatus('idle');
    }
  };

  const handleVerify = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    setVerifying(true);
    setStatus('loading');
    try {
      const response = await auth.verifyEmailCode(normalizedEmail, normalizedCode);
      const nextStatus = response.data?.status || 'invalid';
      setStatus(nextStatus);
      if (nextStatus === 'success' || nextStatus === 'already_verified') {
        clearPendingVerificationEmail();
        toast.success(t('auth.emailVerifiedTitle', 'Email verified'));
      }
    } catch (error) {
      setStatus('invalid');
      toast.error(t('auth.emailCodeVerifyError', 'Could not verify this code.'));
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    if (!normalizedEmail || resending) return;
    setResending(true);
    try {
      await auth.resendVerificationEmail(normalizedEmail);
      setCode('');
      setStatus('idle');
      toast.success(t('auth.verificationResent', 'A new verification code has been sent if the account is unverified.'));
    } catch (error) {
      toast.error(t('auth.verificationResendError', 'Could not resend the verification email.'));
    } finally {
      setResending(false);
    }
  };

  return (
    <VerificationShell>
      <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full border border-lime-300/25 bg-lime-300/10 text-lime-300">
        <MailCheck size={34} strokeWidth={1.7} />
      </div>
      <div className="text-center">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-lime-200">
          {t('auth.checkEmailKicker', 'Registration complete')}
        </p>
        <h1 className="mt-3 text-3xl font-black tracking-normal text-white">
          {t('auth.checkEmailTitle', 'Check your email')}
        </h1>
        <p className="mt-3 text-sm leading-6 text-zinc-300">
          {!wasSent
            ? t('auth.checkEmailSendFallback', 'We created your account, but the confirmation email could not be sent. Try resending it below.')
            : email
            ? t('auth.checkEmailBodyWithEmail', 'We sent a confirmation code to {{email}}. Enter it below to verify your Tennis Diary account.', { email })
            : t('auth.checkEmailBody', 'Enter your email and the confirmation code to verify your Tennis Diary account.')}
        </p>
        {normalizedEmail ? (
          <p className="mt-4 rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-white">
            {normalizedEmail}
          </p>
        ) : null}
      </div>
      <form className="mt-7 grid gap-4" onSubmit={handleVerify}>
        <Field label={t('auth.email')} required>
          <Input
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              if (status !== 'idle') {
                setStatus('idle');
              }
            }}
            required
          />
        </Field>
        <Field label={t('auth.emailVerificationCode', 'Verification code')} required>
          <Input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={handleCodeChange}
            placeholder="000000"
            maxLength={6}
            required
          />
        </Field>
        {status === 'loading' ? (
          <div className="rounded-lg border border-cyan-200/25 bg-cyan-200/10 px-4 py-3 text-sm text-cyan-100">
            {t('auth.emailCodeLoading', 'Confirming your email code...')}
          </div>
        ) : null}
        {isVerified ? (
          <div className="rounded-lg border border-lime-300/25 bg-lime-300/10 px-4 py-3 text-sm text-lime-100">
            {t('auth.emailVerifiedBody', 'Your email is confirmed. You can continue using Tennis Diary.')}
          </div>
        ) : null}
        {status === 'expired' ? (
          <div className="rounded-lg border border-amber-200/25 bg-amber-200/10 px-4 py-3 text-sm text-amber-100">
            {t('auth.emailCodeExpired', 'This code has expired. Request a new code and try again.')}
          </div>
        ) : null}
        {status === 'invalid' ? (
          <div className="rounded-lg border border-red-200/25 bg-red-200/10 px-4 py-3 text-sm text-red-100">
            {t('auth.emailCodeInvalid', 'The code is invalid or has already been used.')}
          </div>
        ) : null}
        <Button type="submit" variant="primary" size="lg" block loading={verifying} disabled={!canSubmit}>
          {t('auth.confirmEmail', 'Confirm email')}
        </Button>
      </form>
      <div className="mt-7 grid gap-3">
        <Button as={Link} to="/dashboard" variant="primary" size="lg" block rightIcon={<ArrowRight size={18} strokeWidth={1.7} />}>
          {t('auth.continueToDashboard', 'Continue to dashboard')}
        </Button>
        <Button type="button" variant="secondary" size="lg" block loading={resending} disabled={!normalizedEmail} onClick={handleResend}>
          {t('auth.resendVerificationEmail', 'Resend verification code')}
        </Button>
      </div>
    </VerificationShell>
  );
};

export const EmailVerificationPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [status, setStatus] = useState(token ? 'loading' : 'invalid');

  useEffect(() => {
    let active = true;
    if (!token) {
      setStatus('invalid');
      return () => {
        active = false;
      };
    }

    const verify = async () => {
      try {
        const response = await auth.verifyEmail(token);
        if (active) {
          setStatus(response.data?.status || 'invalid');
        }
      } catch (error) {
        if (active) {
          setStatus('invalid');
        }
      }
    };

    verify();
    return () => {
      active = false;
    };
  }, [token]);

  const view = useMemo(() => getStatusView(status, t), [status, t]);

  return (
    <VerificationShell>
      <div className={`mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full border ${view.tone}`}>
        {view.icon}
      </div>
      <div className="text-center">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-lime-200">
          {t('auth.emailVerificationKicker', 'Email verification')}
        </p>
        <h1 className="mt-3 text-3xl font-black tracking-normal text-white">{view.title}</h1>
        <p className="mt-3 text-sm leading-6 text-zinc-300">{view.body}</p>
      </div>
      <div className="mt-7 grid gap-3">
        <Button as={Link} to="/dashboard" variant="primary" size="lg" block rightIcon={<ArrowRight size={18} strokeWidth={1.7} />}>
          {t('auth.continueToDashboard', 'Continue to dashboard')}
        </Button>
        <Button as={Link} to="/login" variant="secondary" size="lg" block>
          {t('auth.backToSignIn', 'Back to sign in')}
        </Button>
      </div>
    </VerificationShell>
  );
};

export default EmailVerificationPage;
